<script setup>
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import KeywordPill from "@/components/actions/partials/settings/KeywordPill.vue";
import ContentWizardSuggestion from "@/components/actions/partials/settings/ContentWizardSuggestion.vue";
import ContentWizardVoiceTabs from "@/components/actions/partials/settings/ContentWizardVoiceTabs.vue";
import {useStore} from "vuex";
import {computed, ref, watch} from "vue";
import InputWithButton from "@/components/global-themed/inputs/InputWithButton.vue";
import {useMutation, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import {onClickOutside} from "@vueuse/core";
import ContentWizardSuggestionLoading from "@/components/actions/partials/settings/ContentWizardSuggestionLoading.vue";
import {errorToast, successToast} from "@/composables/useToasts";

const props = defineProps({
  data: {
    required: true,
    type: Object,
  },
  pageId: {
    required: true,
    type: Number,
  },
  loading: {
    required: true,
    type: Boolean,
  },
});

const emit = defineEmits(['runContentWizard']);

const editing = ref(false);
const store = useStore();
const activeTab = computed(() => store.getters['actions/contentWizardActiveTab']);
const activeTabData = computed(() => props.data?.content[activeTab.value]);
const websiteId = computed(() => props?.data?.website?.id ?? null);

const queryClient = useQueryClient();

const {mutate: handleUpdateCompanyName, isPending} = useMutation({
  mutationFn: (name) => axios.patch(`/ajax/v2/website/${websiteId.value}/name/update`, {
    name,
  }),
  onMutate: async (name) => {
    await queryClient.cancelQueries({queryKey: ['content-wizard-index']});

    const previousData = queryClient.getQueryData(['content-wizard-index', props.pageId]);

    queryClient.setQueryData(['content-wizard-index', props.pageId], (old) => ({
      ...old,
      website: {
        id: old.website.id,
        name,
      }
    }));

    successToast('Company name successfully updated');

    editing.value = false;

    return {previousData}
  },
  onError: (error) => {
    const {message} = error.response.data;
    errorToast(message ?? 'Something went wrong');

    editing.value = true;
  },
  onSettled: () => {
    queryClient.invalidateQueries({queryKey: ['content-wizard-index']});
  },
});

const updateButtonInput = ref('');
const companyName = ref('');

watch(
  () => props.data.website.name,
  () => companyName.value = props.data.website.name,
  {
    immediate: true,
});

onClickOutside(updateButtonInput, () => editing.value = false);
</script>

<template>
  <div class="tw-px-8 tw-pb-8 tw-flex tw-flex-col tw-gap-8 tw-justify-center">
    <div class="tw-p-8 tw-border tw-rounded-lg tw-border-grey-mid tw-flex tw-flex-col tw-gap-6">
      <span class="tw-text-sm tw-text-grey">
        Company name
      </span>

      <div
        v-if="!editing"
        class="tw-flex tw-justify-between tw-items-center"
      >
        <h2 class="tw-text-grey-dark tw-text-lg tw-font-bold">
          {{ data.website.name }}
        </h2>

        <DefaultButton
          class="!tw-py-2 !tw-px-4"
          variant="primary-inverted"
          @click="editing = true"
        >
          Update Name
        </DefaultButton>
      </div>

      <div v-show="editing">
        <InputWithButton
          ref="updateButtonInput"
          v-model="companyName"
          :disabled="isPending"
          button-text="Update"
          @submit="handleUpdateCompanyName"
        />
      </div>
    </div>

    <div class="tw-p-8 tw-border tw-rounded-lg tw-border-grey-mid tw-flex tw-flex-col tw-gap-6">
      <span class="tw-text-sm tw-text-grey">
        Keywords
      </span>

      <div class="tw-flex tw-flex-wrap tw-gap-2 tw-overflow-y-scroll tw-max-h-[136px] visible-scrollbar">
        <KeywordPill
          v-for="(keyword, index) in data.keywords"
          :key="index"
          :text="keyword"
        />
      </div>
    </div>

    <div class="tw-p-8 tw-border tw-rounded-lg tw-border-grey-mid tw-flex tw-flex-col tw-gap-6">
      <ContentWizardVoiceTabs />

      <template
        v-if="loading"
      >
        <ContentWizardSuggestionLoading
          label="Title Tag"
        />
        <ContentWizardSuggestionLoading
          label="Meta Description"
          width="tw-w-10/12"
        />
        <ContentWizardSuggestionLoading
          label="H1 Heading"
          width="tw-w-8/12"
        />
      </template>

      <template v-else>
        <ContentWizardSuggestion
          label="Title Tag"
          :value="activeTabData['seo title tag']"
        />
        <ContentWizardSuggestion
          label="Meta Description"
          :value="activeTabData['meta description']"
        />
        <ContentWizardSuggestion
          label="H1 Heading"
          :value="activeTabData['h1 heading']"
        />
      </template>
    </div>

    <div class="tw-flex tw-justify-center tw-items-center tw-flex-col tw-gap-3">
      <DefaultButton
        variant="primary-inverted"
        class="tw-self-center"
        :disabled="loading"
        @click="emit('runContentWizard')"
      >
        <span class="tw-font-semibold tw-antialiased">Re-run Content Wizard</span>
        <svg-vue
          icon="icons/circled-pencil"
          class="tw-w-6 tw-h-6 tw-ml-2"
        />
      </DefaultButton>
    </div>
  </div>
</template>
